<!--
 * @Description: 工具选择弹窗组件（图片工具/视频工具）
 * @FilePath: /weryai-web/components/chatCreation/ToolsSelectModal.vue
-->
<template>
    <Teleport to="body">
        <Transition name="modal-fade">
            <div
                v-if="visible"
                class="tools-modal-overlay"
                role="dialog"
                aria-modal="true"
                :aria-label="type === 'IMAGE' ? t('menus.imageTools') : t('menus.videoTools')"
                @click.self="handleClose"
            >
                <div class="tools-modal">
                    <!-- 头部：Figma 15392:27747 -->
                    <div class="modal-header">
                        <h3 class="modal-title" id="tools-modal-title">
                            {{ type === 'IMAGE' ? t('menus.imageTools') : t('menus.videoTools') }}
                        </h3>
                        <button
                            type="button"
                            class="close-btn"
                            :aria-label="t('common.close')"
                            @click="handleClose"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="22"
                                height="22"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                aria-hidden="true"
                            >
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>

                    <div class="modal-body">
                        <div class="tools-grid" role="listbox" :aria-labelledby="'tools-modal-title'">
                            <!-- 弹窗内统一用设计稿卡片底色（Figma 15392:27747），与外层 mobile_card_bgC 渐变区分 -->
                            <div
                                v-for="tool in toolsList"
                                :key="tool.id"
                                class="tool-item"
                                :class="{ 'is-selected': selectedTool?.id === tool.id }"
                                role="option"
                                :aria-selected="selectedTool?.id === tool.id"
                                @click="handleSelect(tool)"
                            >
                                <img
                                    :src="tool.pc_card_icon || placeholderImage"
                                    :alt="''"
                                    class="tool-icon"
                                    loading="lazy"
                                />
                                <span class="tool-title">{{ tool.title }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button type="button" class="btn btn-cancel" @click="handleClose">
                            {{ t('templateModal.cancel') }}
                        </button>
                        <button
                            type="button"
                            class="btn btn-confirm"
                            :disabled="!selectedTool"
                            @click="handleConfirm"
                        >
                            {{ t('templateModal.save') }}
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { useI18n } from '~/composables/useI18n'
import { useToolsHooks } from '~/utils/useHooks'

interface ToolItem {
    id: string
    path: string
    title: string
    desc?: string
    thumbnail?: string
    video?: string
    type?: string
    isModal?: boolean
    mobile_card_bgC?: string
    mobile_card_icon?: string
    pc_card_icon?: string
}

interface Props {
    visible: boolean
    type?: 'IMAGE' | 'VIDEO'
    modelValue?: string | null
}

interface Emits {
    (e: 'update:visible', value: boolean): void
    (e: 'update:modelValue', value: string | null): void
    (e: 'confirm', tool: ToolItem | null): void
}

const props = withDefaults(defineProps<Props>(), {
    type: 'IMAGE',
    modelValue: null,
})

const emit = defineEmits<Emits>()

const { t } = useI18n()
const { aiImageToolsEnums, aiVideoToolsEnums } = useToolsHooks()

// 占位图片
const placeholderImage = 'https://static.weryai.com/cms/tcbgchange.webp'

// 当前选中的工具路径
const selectedPath = ref<string | null>(props.modelValue)

// 当前选中的工具对象
const selectedTool = ref<ToolItem | null>(null)

// 根据类型获取工具列表（优化颜色分布）
const toolsList = computed(() => {
    const items = props.type === 'IMAGE' 
        ? aiImageToolsEnums.value as ToolItem[]
        : aiVideoToolsEnums.value as ToolItem[]
    
    if (props.type === 'IMAGE') {
        // 按用户指定顺序排列图片工具
        const optimizedOrder = [
            'image-background-changer',    // 图片背景更换
            'image-resizer',               // AI 尺寸调整
            'photo-restoration',           // 老照片修复
            'image-prompt-inversion',      // 提示词反推
            'image-text-translate',        // AI 图片翻译
            'image-upscaler',              // AI 高质增强
            'ai-image-extender',           // AI 图片扩图
            'photo-face-swap',             // 人脸替换
            'remove-text-image',           // 智能消除笔
            'ai-image-background-remover', // AI图片背景移除
        ]
        const itemMap = new Map(items.map(item => [item.id, item]))
        return optimizedOrder
            .map(id => itemMap.get(id))
            .filter(Boolean) as ToolItem[]
    }
    
    return items
})

// 监听 visible 变化
watch(
    () => props.visible,
    (newVal) => {
        if (newVal) {
            // 打开弹窗时重置选中状态
            selectedPath.value = props.modelValue
            if (props.modelValue) {
                selectedTool.value = toolsList.value.find(t => t.path === props.modelValue) || null
            } else {
                selectedTool.value = null
            }
        }
    }
)

// 监听 modelValue 变化
watch(
    () => props.modelValue,
    (newVal) => {
        selectedPath.value = newVal
        if (newVal) {
            selectedTool.value = toolsList.value.find(t => t.path === newVal) || null
        } else {
            selectedTool.value = null
        }
    }
)

// 选择工具
const handleSelect = (tool: ToolItem) => {
    selectedPath.value = tool.path || null
    selectedTool.value = tool
}

// 关闭弹窗
const handleClose = () => {
    emit('update:visible', false)
}

// 确认选择
const handleConfirm = () => {
    if (selectedTool.value) {
        emit('update:modelValue', selectedPath.value)
        emit('confirm', selectedTool.value)
    }
    handleClose()
}
</script>

<style scoped lang="less">
/* Figma 15392:27747 — 图片/视频工具选择弹窗 */
@modal-bg: #1e1e1e;
@modal-radius: 20px;
@accent: #99f768;

.tools-modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 3000;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    min-height: 100dvh;
    padding: 16px;
    box-sizing: border-box;
    background: rgba(0, 0, 0, 0.65);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
}

.tools-modal {
    position: relative;
    width: 960px;
    max-width: min(960px, 100%);
    max-height: min(78vh, 720px);
    display: flex;
    flex-direction: column;
    border-radius: @modal-radius;
    border: 1px solid rgba(255, 255, 255, 0.08);
    background: @modal-bg;
    box-shadow: 0 24px 80px rgba(0, 0, 0, 0.55);
    overflow: hidden;
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    padding: 28px 32px 20px;
}

.modal-title {
    font-size: 18px;
    font-weight: 600;
    line-height: 1.3;
    color: #fff;
    margin: 0;
    letter-spacing: 0.02em;
}

.close-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    background: transparent;
    border: none;
    border-radius: 10px;
    color: rgba(255, 255, 255, 0.85);
    cursor: pointer;
    transition:
        background 0.2s ease,
        color 0.2s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.08);
        color: #fff;
    }
}

.modal-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 4px 32px 24px;

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.18);
        border-radius: 3px;

        &:hover {
            background: rgba(255, 255, 255, 0.28);
        }
    }
}

.tools-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;

    @media (max-width: 900px) {
        grid-template-columns: repeat(3, 1fr);
        gap: 14px;
    }

    @media (max-width: 640px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }

    @media (max-width: 380px) {
        grid-template-columns: 1fr;
    }
}

.tool-item {
    position: relative;
    box-sizing: border-box;
    display: flex;
    height: 98px;
    padding: 14px;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-end;
    flex: 1 0 0;
    border-radius: 14px;
    border: 1px solid rgba(255, 255, 255, 0.05);
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.05) 0%,
        rgba(255, 255, 255, 0.1) 100%
    );
    box-shadow:
        0 10px 15px -3px rgba(0, 0, 0, 0.1),
        0 4px 6px -4px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition:
        transform 0.2s ease,
        box-shadow 0.2s ease,
        border-color 0.2s ease;

    .tool-icon {
        width: 40px;
        height: 40px;
        border-radius: 8px;
        object-fit: contain;
        flex-shrink: 0;
    }

    /* 设计稿为 align-items: flex-end；标题仍占满底行左对齐 */
    .tool-title {
        align-self: stretch;
        width: 100%;
        font-size: 14px;
        font-weight: 600;
        line-height: 1.35;
        color: #fff;
        text-align: left;
    }

    &:hover {
        transform: translateY(-2px);
        box-shadow:
            0 12px 18px -3px rgba(0, 0, 0, 0.12),
            0 6px 8px -4px rgba(0, 0, 0, 0.12);
    }

    &.is-selected {
        border: 1px solid @accent;
        box-shadow:
            0 10px 15px -3px rgba(0, 0, 0, 0.1),
            0 4px 6px -4px rgba(0, 0, 0, 0.1);
    }
}

.modal-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-shrink: 0;
    gap: 10px;
    padding: 16px 32px 28px;
}

/* 设计稿：104×40、padding 10、圆角 8、主色 #99F768 */
.btn {
    box-sizing: border-box;
    display: flex;
    width: 104px;
    min-width: 104px;
    height: 40px;
    padding: 10px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
    cursor: pointer;
    transition:
        background 0.2s ease,
        color 0.2s ease,
        border-color 0.2s ease,
        opacity 0.2s ease;

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
}

.btn-cancel {
    font-weight: 500;
    background: #2a2a2e;
    color: rgba(255, 255, 255, 0.75);
    border: 1px solid rgba(255, 255, 255, 0.12);

    &:hover:not(:disabled) {
        background: #35353a;
        color: #fff;
    }
}

.btn-confirm {
    font-weight: 600;
    background: #99f768;
    color: #0a0a0a;
    border: none;

    &:hover:not(:disabled) {
        background: #aaf87a;
    }
}

// 过渡动画
.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.3s ease;

    .tools-modal {
        transition: transform 0.3s ease;
    }
}

.modal-fade-enter-from,
.modal-fade-leave-to {
    opacity: 0;

    .tools-modal {
        transform: scale(0.95);
    }
}
</style>
